import PlayerFactory from "youtube-player";

import { RefObject, useEffect, useMemo, useState } from "react";
import { useHasMounted } from "./useHasMounted";

export type YouTubeController = {
  getDuration: () => Promise<number>;
  pause: () => Promise<void>;
  play: () => Promise<void>;
};

// youtube-player doesn't ship its own types, so we need to wrangle the
// exported ones a little bit
type YouTubePlayer = ReturnType<typeof PlayerFactory>;
// Options type isn't exposed at all, so we stub the options we care about
type PlayerOptions = {
  videoId?: string;
};

export type UseYouTubeArgs = {
  videoContainerRef: RefObject<HTMLElement>;
  videoId: string;
};

export function useYouTube(args: UseYouTubeArgs): YouTubeController | null {
  const { videoId, videoContainerRef } = args;
  const [player, setPlayer] = useState<YouTubePlayer | null>(null);
  // we use hasMounted to make sure this hook (re-)runs after the first render
  // cycle, and the videoContainer is mounted
  const hasMounted = useHasMounted();

  useEffect(() => {
    // initialize the player
    if (hasMounted && player === null) {
      const containerElement = videoContainerRef.current;
      if (!containerElement) {
        return;
      }
      const options: PlayerOptions = {
        videoId: videoId,
      };
      // this creates the embedded iframe in the target container, as well as
      // exposes the player api
      const p = PlayerFactory(containerElement, options, false);
      setPlayer(p);
    }
  }, [player, hasMounted, videoContainerRef, videoId]);

  const controller = useMemo<YouTubeController | null>(() => {
    if (player === null) {
      return null;
    }

    return {
      getDuration: () => player.getDuration(),
      play: () => player.playVideo(),
      pause: () => player.pauseVideo(),
    };
  }, [player]);

  return controller;
}

export default useYouTube;
